import {
  Bleed,
  Box,
  ButtonIcon,
  IconClear,
  IconCritical,
  Stack,
  Text,
} from 'braid-design-system';
import { useCombobox, useMultipleSelection } from 'downshift';
import { useRef, type ReactNode, useId } from 'react';

import type { MultiselectAutosuggestProps, BaseDownshiftItem } from './types';

import * as styles from './styles.css';

const mergeAccessibilityIds = (
  ids: Array<string | undefined>,
): string | undefined => {
  const filteredIds = ids.filter(Boolean);

  if (filteredIds.length === 0) {
    return undefined;
  }

  return filteredIds.join(' ');
};

export const MultiselectAutosuggest = <T extends BaseDownshiftItem>({
  id,
  size = 'standard',
  label,
  message,
  tone = 'neutral',
  suggestions,
  selectedItems,
  clearLabel,
  placeholder,
  searchText,
  hideLabel = false,
  dropdownBoxProps = {
    background: 'surface',
    borderRadius: 'standard',
    width: 'full',
    zIndex: 'dropdown',
    boxShadow: 'medium',
    position: 'absolute',
  },
  setSearchText,
  onClickSuggestionItem,
  onItemsSelected,
  renderSelectedItems,
  renderSuggestions,
  onMenuClose,
  isItemDisabled,
  onFocus,
  description,
}: MultiselectAutosuggestProps<T>): ReactNode => {
  const inputRef = useRef<HTMLInputElement>(null);
  const descriptionId = useId();
  const messageId = useId();

  const { getSelectedItemProps, getDropdownProps } = useMultipleSelection({
    selectedItems,
    onStateChange: ({ type, selectedItems: newSelectedItems }) => {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          onItemsSelected(newSelectedItems ?? []);
          break;
        default:
          break;
      }
    },
  });

  const {
    isOpen,
    highlightedIndex,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    openMenu,
  } = useCombobox({
    items: suggestions,
    itemToString: (item) => (item ? item.id : ''),
    inputValue: searchText,
    isItemDisabled: isItemDisabled ?? (() => false),
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          const newChanges = {
            ...changes,
            isOpen: true, // keeps the menu open after selection.
            highlightedIndex: state.highlightedIndex,
          };
          return newChanges;
        case useCombobox.stateChangeTypes.InputClick:
          return {
            ...changes,
            isOpen: true,
          };
        default:
          return changes;
      }
    },
    onStateChange({ inputValue: newInputValue, type, selectedItem }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          if (onMenuClose) {
            onMenuClose();
          }
          if (selectedItem) {
            onClickSuggestionItem(selectedItem);
          }

          setSearchText('');
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          if (onMenuClose) {
            onMenuClose();
          }
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            onClickSuggestionItem(selectedItem);
          }
          break;

        case useCombobox.stateChangeTypes.InputChange:
          setSearchText(newInputValue ?? '');
          break;
        default:
          break;
      }
    },
  });

  const hasSelectedItems = selectedItems.length > 0;
  const shouldShowClearIcon = hasSelectedItems || searchText.length > 0;

  const handleClearClick = () => {
    onItemsSelected([]);
    setSearchText('');
  };

  const shouldShowLabelOrDescription = (label && !hideLabel) || description;

  return (
    <Box
      id={`${id}-multiselect-autosuggest`}
      display="flex"
      flexDirection="column"
      position="relative"
      width="full"
      className={shouldShowLabelOrDescription ? styles.gap[size] : undefined}
    >
      {shouldShowLabelOrDescription && (
        <Stack space="small">
          <Box
            display={hideLabel ? 'none' : 'block'}
            component="label"
            {...getLabelProps({
              onClick: () => {
                openMenu();
              },
            })}
          >
            <Text size={size} weight="strong">
              {label}
            </Text>
          </Box>
          {description ? (
            <Text tone="secondary" id={descriptionId} size={size}>
              {description}
            </Text>
          ) : null}
        </Stack>
      )}
      <Box>
        <Box
          position="relative"
          background="surface"
          width="full"
          onClick={(e) => {
            openMenu();
            e.stopPropagation();
          }}
          className={{
            [styles.inputParent]: true,
            [styles.inputParentBorderColor[tone]]: true,
            [styles.noClearIconSpace]: shouldShowClearIcon,
            [styles.paddingY[size]]: true,
          }}
        >
          {renderSelectedItems
            ? renderSelectedItems({ getSelectedItemProps })
            : null}

          <input
            className={`${
              isOpen ||
              searchText ||
              (!searchText && selectedItems.length === 0)
                ? styles.flex
                : styles.hidden
            } ${size === 'small' ? styles.smallText : styles.standardText} ${
              styles.input
            }`}
            placeholder={hasSelectedItems ? '' : placeholder}
            aria-describedby={mergeAccessibilityIds([
              description ? descriptionId : undefined,
              message ? messageId : undefined,
            ])}
            onFocus={onFocus}
            {...getInputProps(
              getDropdownProps({ ref: inputRef, value: searchText }),
              {
                suppressRefError: true,
              },
            )}
          />
          {shouldShowClearIcon ? (
            <Box
              component="span"
              position="absolute"
              width="touchable"
              height="touchable"
              display="flex"
              alignItems="center"
              justifyContent="center"
              right={0}
            >
              <Bleed vertical="xxsmall">
                <ButtonIcon
                  id={`${id}-clear-multiselect-autosuggest-input`}
                  label={clearLabel || 'Clear'}
                  variant="transparent"
                  tone="neutral"
                  type="reset"
                  onClick={handleClearClick}
                  icon={<IconClear />}
                />
              </Bleed>
            </Box>
          ) : null}
        </Box>
        <Box position="relative">
          <Box
            component="ul"
            position="relative"
            display={isOpen ? 'block' : 'none'}
            {...dropdownBoxProps}
            className={styles.suggestionList}
            {...getMenuProps({}, { suppressRefError: true })}
          >
            {renderSuggestions({ highlightedIndex, getItemProps })}
          </Box>
        </Box>
        {message ? (
          <Box paddingTop="xsmall" position="relative">
            <Text
              id={messageId}
              tone={tone === 'neutral' ? 'secondary' : tone}
              icon={
                tone === 'critical' ? (
                  <IconCritical tone="critical" />
                ) : undefined
              }
              size="small"
            >
              {message}
            </Text>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
